<template>
  <teleport to="body">
    <div
      v-for="position in positions"
      :key="position"
      class="toast-host"
      :class="`toast-host--${position}`"
      aria-live="polite"
      aria-relevant="additions"
    >
      <transition-group :name="`toast-list-${position}`">
        <section
          v-for="item in messagesFor(position)"
          :key="item.id"
          class="toast-item"
          :class="`is-${item.type}`"
          role="status"
        >
          <i class="toast-spark toast-spark--one" aria-hidden="true">✦</i>
          <i class="toast-spark toast-spark--two" aria-hidden="true">✦</i>
          <span class="toast-icon" aria-hidden="true">
            <component :is="iconMap[item.type] || Info" :size="18" stroke-width="2.4" />
            <b></b>
          </span>
          <div class="toast-copy">
            <strong v-if="item.title">{{ item.title }}</strong>
            <p>{{ item.message }}</p>
          </div>
          <button type="button" class="toast-close" aria-label="关闭提示" @click="toast.remove(item.id)">
            <X :size="17" />
          </button>
        </section>
      </transition-group>
    </div>
  </teleport>
</template>

<script setup>
import { AlertTriangle, Check, CircleAlert, Info, X } from "@lucide/vue";
import { useToast } from "../composables/useToast";

const { messages, toast } = useToast();
const positions = ["center", "top", "right", "left"];
const iconMap = {
  success: Check,
  error: CircleAlert,
  warning: AlertTriangle,
  info: Info,
};

function messagesFor(position) {
  return messages.value.filter((item) => item.position === position);
}
</script>

<style scoped>
.toast-host{
  position:fixed;
  z-index:800;
  width:min(390px,calc(100vw - 24px));
  display:grid;
  gap:10px;
  pointer-events:none;
}
.toast-host--center{
  top:50%;
  left:50%;
  transform:translate(-50%,-50%);
}
.toast-host--top{
  top:clamp(76px,9vw,108px);
  left:50%;
  transform:translateX(-50%);
}
.toast-host--right{
  top:50%;
  right:clamp(12px,2.6vw,30px);
  transform:translateY(-50%);
}
.toast-host--left{
  top:50%;
  left:clamp(12px,2.6vw,30px);
  transform:translateY(-50%);
}
.toast-item{
  position:relative;
  overflow:hidden;
  display:grid;
  grid-template-columns:38px minmax(0,1fr) 30px;
  align-items:center;
  gap:11px;
  padding:13px 12px;
  border:1px solid rgba(114,92,255,.16);
  border-radius:22px 22px 22px 9px;
  background:linear-gradient(135deg,rgba(255,255,255,.98),rgba(250,247,255,.98));
  color:#3b2d29;
  box-shadow:0 18px 44px rgba(76,62,139,.18),0 4px 0 rgba(114,92,255,.06);
  backdrop-filter:blur(12px);
  pointer-events:auto;
}
.toast-item::before{
  content:"";
  position:absolute;
  width:86px;
  height:86px;
  right:-38px;
  bottom:-52px;
  border-radius:50%;
  background:rgba(112,92,255,.08);
}
.toast-item.is-success{border-color:rgba(46,157,120,.24)}
.toast-item.is-error{border-color:rgba(217,75,85,.24)}
.toast-item.is-warning{border-color:rgba(217,138,36,.26)}
.toast-item.is-info{border-color:rgba(98,84,245,.22)}
.toast-icon{
  position:relative;
  width:34px;
  height:34px;
  display:grid;
  place-items:center;
  border-radius:50% 50% 46% 54%;
  color:#fff;
  background:#5795bd;
  font-weight:900;
  box-shadow:inset 0 -3px 0 rgba(0,0,0,.1),0 6px 12px rgba(91,73,160,.16);
  animation:toast-bean-wave .55s cubic-bezier(.2,.9,.2,1.2);
}
.toast-icon b{position:absolute;right:-2px;bottom:1px;width:8px;height:8px;border:2px solid #fff;border-radius:50%;background:inherit}
.is-success .toast-icon{background:#52a675}
.is-error .toast-icon{background:#d94b55}
.is-warning .toast-icon{background:#d69832}
.toast-copy{min-width:0}
.toast-copy strong{display:block;margin-bottom:3px;font-size:14px}
.toast-copy p{margin:0;color:#755b4e;font-size:14px;line-height:1.55;overflow-wrap:anywhere}
.toast-close{
  width:30px;
  height:30px;
  display:grid;
  place-items:center;
  border:0;
  background:transparent;
  color:#9b7c6d;
  cursor:pointer;
}
.toast-spark{position:absolute;z-index:2;color:#ffbb44;font-size:11px;font-style:normal;pointer-events:none;animation:toast-sparkle 1.25s ease-in-out infinite}
.toast-spark--one{top:7px;left:7px}.toast-spark--two{right:42px;bottom:6px;animation-delay:.38s}
.toast-list-center-enter-active,.toast-list-center-leave-active,
.toast-list-top-enter-active,.toast-list-top-leave-active,
.toast-list-right-enter-active,.toast-list-right-leave-active,
.toast-list-left-enter-active,.toast-list-left-leave-active{
  transition:opacity .2s ease,transform .22s ease;
}
.toast-list-center-enter-active,.toast-list-top-enter-active{animation:toast-pop .38s cubic-bezier(.2,.95,.25,1.22)}
.toast-list-center-enter-from,.toast-list-center-leave-to{opacity:0;transform:scale(.86) translateY(10px)}
.toast-list-top-enter-from,.toast-list-top-leave-to{opacity:0;transform:translateY(-14px)}
.toast-list-right-enter-from,.toast-list-right-leave-to{opacity:0;transform:translateX(18px)}
.toast-list-left-enter-from,.toast-list-left-leave-to{opacity:0;transform:translateX(-18px)}
@media (max-width:640px){
  .toast-host{left:12px;right:12px;width:auto}
  .toast-host--center{left:50%;right:auto;width:calc(100vw - 24px)}
  .toast-host--top{top:72px;left:12px;transform:none}
  .toast-host--right,.toast-host--left{left:12px;right:12px;transform:translateY(-50%)}
}

.toast-item{color:var(--text)}
.toast-copy p{color:var(--muted)}
.toast-close{color:var(--muted)}
.toast-icon{border-radius:50% 50% 46% 54%;background:var(--ui-primary)}
.is-success .toast-icon{background:var(--green)}
.is-error .toast-icon{background:var(--brand)}
.is-warning .toast-icon{color:var(--text);background:var(--yellow)}
@keyframes toast-pop{0%{opacity:0;transform:scale(.72) translateY(18px)}65%{opacity:1;transform:scale(1.045) translateY(-2px)}100%{transform:scale(1) translateY(0)}}
@keyframes toast-bean-wave{0%{transform:rotate(-18deg) scale(.72)}65%{transform:rotate(8deg) scale(1.08)}100%{transform:rotate(0) scale(1)}}
@keyframes toast-sparkle{0%,100%{opacity:.28;transform:scale(.6) rotate(0)}50%{opacity:1;transform:scale(1.18) rotate(28deg)}}
@media(prefers-reduced-motion:reduce){.toast-list-center-enter-active,.toast-list-top-enter-active,.toast-icon,.toast-spark{animation:none}}
</style>
